import React, { useMemo, useState } from "react";
import { ChevronDown, Plus, Trash2, X } from "lucide-react";
import InvoiceEditor from "./InvoiceEditor";
import {
  buildOrderInvoiceFlow,
  billabilityStatusClass,
  billabilityStatusLabel,
  formatDate,
  formatMoney,
  formatQty,
  getLineAmount,
  invoiceStatusLabel,
  isSentInvoiceStatus,
  parseDecimal,
  statusClass,
} from "./billingMath";

function LineCard({ node, focused, dimmed, onClick }) {
  const isFullyInvoiced = node.amount > 0 && node.invoicedAmount >= node.amount - 0.01;
  const hasSentInvoice = node.invoicedAmount > 0.01;
  const hasPlannedInvoice = node.plannedAmount > 0.01;
  const hasUnlinked = node.unlinkedAmount > 0.01;
  const stateLabel = isFullyInvoiced
    ? "Facturée"
    : hasSentInvoice
      ? "Partiel envoyé"
      : hasPlannedInvoice && !hasUnlinked
        ? "Prévue"
        : hasPlannedInvoice
          ? "Partiel prévu"
          : "Non liée";
  const stateClass = isFullyInvoiced || hasSentInvoice
    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
    : hasPlannedInvoice
      ? "border-amber-200 bg-amber-50 text-amber-700"
      : "border-slate-200 bg-slate-50 text-slate-500";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full flex-col gap-1 rounded-xl border px-3 py-2 text-left transition ${
        focused ? "border-indigo-400 bg-indigo-50 ring-2 ring-indigo-300" : "border-indigo-100 bg-indigo-50/60 hover:border-indigo-300"
      } ${dimmed ? "opacity-30" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-950">{node.label}</div>
          <div className="truncate text-[11px] text-slate-500">{node.sublabel}</div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-xs font-semibold text-indigo-700">{formatMoney(node.amount)}</div>
          <div className="text-[11px] text-slate-500">{formatQty(node.remaining)} non lié</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1 pt-1">
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${stateClass}`}>
          {stateLabel}
        </span>
        {node.unlinkedAmount > 0.01 && (
          <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] text-slate-500">
            Non lié {formatMoney(node.unlinkedAmount)}
          </span>
        )}
        {node.plannedAmount > 0.01 && (
          <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] text-amber-700">
            Prévu {formatMoney(node.plannedAmount)}
          </span>
        )}
        {node.invoicedAmount > 0.01 && (
          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-700">
            Facturé {formatMoney(node.invoicedAmount)}
          </span>
        )}
      </div>
    </button>
  );
}

function InvoiceCard({ node, focused, dimmed, stacked, onClick }) {
  const allocations = node.invoice.line_allocations || [];
  const sent = isSentInvoiceStatus(node.invoice.status);
  const billabilityStatus = node.invoice.billability_status || "pending";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full flex-col gap-1 rounded-xl border bg-white px-3 py-2 text-left shadow-sm transition ${
        focused
          ? "border-emerald-400 ring-2 ring-emerald-300"
          : sent
            ? "border-emerald-100 hover:border-emerald-300"
            : "border-amber-100 hover:border-amber-300"
      } ${dimmed ? "opacity-30" : ""}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-950">
            {node.invoice.invoice_reference || node.invoice.label || `Facture ${node.invoice.id}`}
          </div>
          <div className="truncate text-[11px] text-slate-500">
            {formatDate(node.invoice.issue_date || node.invoice.event_date)}
          </div>
          <div className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${billabilityStatusClass(billabilityStatus)}`}>
            {billabilityStatusLabel(billabilityStatus)}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${statusClass(node.invoice.status)}`}>
            {invoiceStatusLabel(node.invoice.status)}
          </div>
          <div className={`mt-1 text-xs font-semibold ${sent ? "text-emerald-700" : "text-amber-700"}`}>
            {formatMoney(node.invoice.amount_eur)}
          </div>
        </div>
      </div>
      {stacked && allocations.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {allocations.map((allocation) => (
            <span
              key={`${node.key}-${allocation.line_id}`}
              className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-600"
            >
              {allocation.line_name || `Ligne ${allocation.line_id}`} · {formatMoney(allocation.allocated_amount_eur)}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

export default function FlowDiagram({
  lines,
  invoiceEvents,
  cashEvents,
  consumedByLine,
  projectOptions,
  iterationOptions,
  clientPaymentTerms,
  newLine,
  setNewLine,
  onAddLine,
  onPatchLine,
  onDeleteLine,
  onUpdateInvoice,
  onUpdateInvoiceBillability,
  onDeleteInvoice,
  showError,
}) {
  const [editingLineId, setEditingLineId] = useState(null);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [showAddLine, setShowAddLine] = useState(false);

  const flow = useMemo(
    () => buildOrderInvoiceFlow(lines, invoiceEvents, consumedByLine),
    [lines, invoiceEvents, consumedByLine],
  );

  const focusKey = editingLineId
    ? `left:${editingLineId}`
    : editingInvoiceId
      ? `right:${editingInvoiceId}`
      : null;

  const focus = useMemo(() => {
    if (!focusKey) return { active: false, nodes: new Set(), flows: new Set() };
    const nodes = new Set([focusKey]);
    const flows = new Set();
    flow.flows.forEach((item) => {
      if (`left:${item.sourceKey}` === focusKey || `right:${item.targetKey}` === focusKey) {
        nodes.add(`left:${item.sourceKey}`);
        nodes.add(`right:${item.targetKey}`);
        flows.add(item.key);
      }
    });
    return { active: true, nodes, flows };
  }, [flow.flows, focusKey]);

  const toggleLine = (lineId) => {
    setEditingInvoiceId(null);
    setEditingLineId((current) => (current === lineId ? null : lineId));
  };
  const toggleInvoice = (invoiceId) => {
    setEditingLineId(null);
    setEditingInvoiceId((current) => (current === invoiceId ? null : invoiceId));
  };

  const editingLine = lines.find((line) => line.id === editingLineId) || null;
  const editingInvoice = invoiceEvents.find((invoice) => invoice.id === editingInvoiceId) || null;

  const handleUpdateInvoice = async (invoice, payload) => {
    const ok = await onUpdateInvoice(invoice, payload);
    if (ok) setEditingInvoiceId(null);
    return ok;
  };
  const handleDeleteInvoiceInner = async (invoice) => {
    const ok = await onDeleteInvoice(invoice);
    if (ok) setEditingInvoiceId(null);
    return ok;
  };

  return (
    <div className="space-y-4">
      {/* Wide layout: bezier diagram */}
      <div className="hidden lg:grid lg:grid-cols-[minmax(0,1fr)_120px_minmax(0,1fr)] lg:items-start lg:gap-3">
        <div className="relative" style={{ height: flow.height }}>
          {flow.leftNodes.map((node) => {
            const selectionKey = `left:${node.key}`;
            return (
              <div
                key={node.key}
                className="absolute left-0 right-0"
                style={{ top: node.y - flow.nodeHeight / 2, minHeight: flow.nodeHeight }}
              >
                <LineCard
                  node={node}
                  focused={focusKey === selectionKey}
                  dimmed={focus.active && !focus.nodes.has(selectionKey)}
                  onClick={() => node.line && toggleLine(node.line.id)}
                />
              </div>
            );
          })}
        </div>

        <svg
          className="w-full overflow-visible"
          viewBox={`0 0 120 ${flow.height}`}
          style={{ height: flow.height }}
          role="img"
          aria-label="Flux entre lignes de commande et factures"
        >
          <defs>
            {/* userSpaceOnUse: sinon les tracés horizontaux (bbox d'aire nulle) ne rendent pas le dégradé */}
            <linearGradient id="order-invoice-flow-gradient" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="120" y2="0">
              <stop offset="0%" stopColor="#4f46e5" />
              <stop offset="100%" stopColor="#059669" />
            </linearGradient>
          </defs>
          {flow.flows.map((item) => {
            const dimmed = focus.active && !focus.flows.has(item.key);
            return (
              <path
                key={item.key}
                d={`M 0 ${item.sourceY} C 40 ${item.sourceY}, 80 ${item.targetY}, 120 ${item.targetY}`}
                fill="none"
                stroke="url(#order-invoice-flow-gradient)"
                strokeLinecap="round"
                strokeWidth={item.width}
                opacity={focus.active ? (dimmed ? 0.06 : 0.6) : 0.32}
              >
                <title>{`${formatMoney(item.amount)} - ${item.lineNames.join(", ")}`}</title>
              </path>
            );
          })}
        </svg>

        <div className="relative" style={{ height: flow.height }}>
          {flow.rightNodes.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
              Aucune facture.
            </div>
          ) : (
            flow.rightNodes.map((node) => {
              const selectionKey = `right:${node.key}`;
              return (
                <div
                  key={node.key}
                  className="absolute left-0 right-0"
                  style={{ top: node.y - flow.nodeHeight / 2, minHeight: flow.nodeHeight }}
                >
                  <InvoiceCard
                    node={node}
                    focused={focusKey === selectionKey}
                    dimmed={focus.active && !focus.nodes.has(selectionKey)}
                    onClick={() => toggleInvoice(node.invoice.id)}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Narrow layout: stacked lists */}
      <div className="space-y-3 lg:hidden">
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Lignes de commande</div>
          {flow.leftNodes.map((node) => (
            <LineCard
              key={node.key}
              node={node}
              focused={focusKey === `left:${node.key}`}
              dimmed={false}
              onClick={() => node.line && toggleLine(node.line.id)}
            />
          ))}
        </div>
        <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
          <ChevronDown className="h-4 w-4" /> réparti en factures
        </div>
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Factures</div>
          {flow.rightNodes.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
              Aucune facture.
            </div>
          ) : (
            flow.rightNodes.map((node) => (
              <InvoiceCard
                key={node.key}
                node={node}
                focused={focusKey === `right:${node.key}`}
                dimmed={false}
                stacked
                onClick={() => toggleInvoice(node.invoice.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Inline editors */}
      {editingLine && (
        <LineInlineEditor
          key={editingLine.id}
          line={editingLine}
          onClose={() => setEditingLineId(null)}
          onPatch={onPatchLine}
          onDelete={async (line) => {
            const ok = await onDeleteLine(line);
            if (ok) setEditingLineId(null);
            return ok;
          }}
        />
      )}

      {editingInvoice && (
        <InvoiceEditor
          key={editingInvoice.id}
          mode="edit"
          invoice={editingInvoice}
          lines={lines}
          cashEvents={cashEvents}
          projectOptions={projectOptions}
          iterationOptions={iterationOptions}
          clientPaymentTerms={clientPaymentTerms}
          onUpdate={handleUpdateInvoice}
          onUpdateBillability={onUpdateInvoiceBillability}
          onDelete={handleDeleteInvoiceInner}
          onCancel={() => setEditingInvoiceId(null)}
          canEditBillability={
            !editingInvoice.billability_is_restricted || Boolean(editingInvoice.can_edit_billability)
          }
          showError={showError}
        />
      )}

      {/* Add line */}
      {showAddLine ? (
        <form
          onSubmit={(event) => {
            onAddLine(event);
            setShowAddLine(false);
          }}
          className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:grid-cols-2 lg:grid-cols-12"
        >
          <input
            className="rounded-lg border border-slate-200 px-2 py-2 text-sm lg:col-span-3"
            placeholder="Libelle ligne"
            value={newLine.name}
            onChange={(event) => setNewLine({ ...newLine, name: event.target.value })}
          />
          <input
            className="rounded-lg border border-slate-200 px-2 py-2 text-sm lg:col-span-2"
            placeholder="PU"
            value={newLine.unit_price}
            onChange={(event) => setNewLine({ ...newLine, unit_price: event.target.value })}
          />
          <input
            className="rounded-lg border border-slate-200 px-2 py-2 text-sm lg:col-span-1"
            placeholder="Qt"
            value={newLine.quantity}
            onChange={(event) => setNewLine({ ...newLine, quantity: event.target.value })}
          />
          <input
            className="rounded-lg border border-slate-200 px-2 py-2 text-sm lg:col-span-2"
            placeholder="Achats budget"
            value={newLine.purchase_budget_eur}
            onChange={(event) => setNewLine({ ...newLine, purchase_budget_eur: event.target.value })}
          />
          <div className="flex gap-2 lg:col-span-4">
            <button className="flex-1 rounded-lg bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800">
              Ajouter
            </button>
            <button
              type="button"
              onClick={() => setShowAddLine(false)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 hover:border-slate-300"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => {
            setEditingLineId(null);
            setEditingInvoiceId(null);
            setShowAddLine(true);
          }}
          className="inline-flex items-center gap-2 rounded-lg border border-dashed border-indigo-300 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
        >
          <Plus className="h-4 w-4" />
          Ajouter une ligne de commande
        </button>
      )}
    </div>
  );
}

function LineInlineEditor({ line, onClose, onPatch, onDelete }) {
  const patchIfChanged = (patch) => onPatch(line, patch);
  return (
    <div className="space-y-3 rounded-xl border border-indigo-200 bg-indigo-50/40 p-3 sm:p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-indigo-800">Éditer la ligne</div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">
            Total&nbsp;: <span className="font-semibold text-slate-950">{formatMoney(getLineAmount(line))}</span>
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-white hover:text-slate-600"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-xs font-medium text-slate-500 sm:col-span-2">
          Libelle
          <input
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900"
            defaultValue={line.name}
            onBlur={(event) => {
              const value = event.target.value.trim();
              if (value && value !== line.name) patchIfChanged({ name: value });
            }}
          />
        </label>
        <label className="text-xs font-medium text-slate-500">
          Prix unitaire
          <input
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-right text-sm text-slate-900"
            defaultValue={line.unit_price}
            onBlur={(event) => {
              const value = parseDecimal(event.target.value);
              if (Number.isFinite(value) && value !== Number(line.unit_price)) patchIfChanged({ unit_price: value });
            }}
          />
        </label>
        <label className="text-xs font-medium text-slate-500">
          Quantite
          <input
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-right text-sm text-slate-900"
            defaultValue={line.quantity}
            onBlur={(event) => {
              const value = parseDecimal(event.target.value);
              if (Number.isFinite(value) && value !== Number(line.quantity)) patchIfChanged({ quantity: value });
            }}
          />
        </label>
        <label className="text-xs font-medium text-slate-500 sm:col-span-2">
          Description
          <input
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900"
            defaultValue={line.description || ""}
            onBlur={(event) => {
              const value = event.target.value.trim();
              if (value !== (line.description || "")) patchIfChanged({ description: value || null });
            }}
          />
        </label>
      </div>
      <div className="flex justify-start">
        <button
          type="button"
          onClick={() => onDelete(line)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50"
        >
          <Trash2 className="h-4 w-4" />
          Supprimer la ligne
        </button>
      </div>
    </div>
  );
}
